import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Tag,
  Clock,
  BookOpen,
  CheckCircle,
  RefreshCw,
  Archive,
  RotateCcw,
  Edit2,
  Trophy,
  Sparkles,
  Trash2,
} from "lucide-react";
import { ExpandableCard } from "../common/ExpandableCard";
import { Answer, UserProfile } from "../../types";
import {
  subscribeToAnswers,
  createAnswer,
  updateAnswer,
  verifyAnswerFreshness,
  retireAnswer,
  restoreAnswer,
  deleteAnswer,
  calculateFreshness,
} from "../../services/answersService";
import { useStaffDirectory } from "../../services/staffService";
import { AnswerModal } from "../library/AnswerModal";
import { DeleteAnswerModal } from "../library/DeleteAnswerModal";
import { ImportProposalModal } from "../library/ImportProposalModal";
import { CopyNarrativeButton } from "../library/CopyNarrativeButton";

interface LibraryViewProps {
  user?: UserProfile | null;
}

export const LibraryView: React.FC<LibraryViewProps> = ({ user }) => {
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<
    "all_active" | "winning" | "review_due" | "retired"
  >("all_active");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState<Answer | null>(null);
  const [deletingAnswer, setDeletingAnswer] = useState<Answer | null>(null);

  const { staff } = useStaffDirectory();

  useEffect(() => {
    const unsubscribe = subscribeToAnswers(
      (items) => {
        setAnswers(items);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load answers:", err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  // Compute available service tags from current answers + defaults
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const ans of answers) {
      for (const t of ans.serviceTags) {
        if (t.trim()) set.add(t.trim());
      }
    }
    return Array.from(set).sort();
  }, [answers]);

  const filteredAnswers = useMemo(() => {
    return answers.filter((ans) => {
      // 1. Status Filter
      if (statusFilter === "all_active" && ans.status === "retired") return false;
      if (statusFilter === "retired" && ans.status !== "retired") return false;
      if (statusFilter === "winning") {
        if (ans.status === "retired") return false;
        if (!ans.wonBidCount || ans.wonBidCount <= 0) return false;
      }
      if (statusFilter === "review_due") {
        if (ans.status === "retired") return false;
        const freshness = calculateFreshness(ans.reviewDueDate, ans.status);
        if (!freshness.isDueSoon && !freshness.isOverdue) return false;
      }

      // 2. Service Tag Filter
      if (selectedTag && !ans.serviceTags.includes(selectedTag)) {
        return false;
      }

      // 3. Search Filter
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesTitle = ans.title.toLowerCase().includes(query);
        const matchesBody = ans.body.toLowerCase().includes(query);
        const matchesOwner = ans.owner.toLowerCase().includes(query);
        const matchesSource = ans.sourceProposal?.toLowerCase().includes(query) ?? false;
        const matchesTags = ans.serviceTags.some((t) => t.toLowerCase().includes(query));

        if (!matchesTitle && !matchesBody && !matchesOwner && !matchesSource && !matchesTags) {
          return false;
        }
      }

      return true;
    });
  }, [answers, statusFilter, selectedTag, searchTerm]);

  const handleSaveAnswer = async (data: Omit<Answer, "id" | "createdAt" | "updatedAt">) => {
    if (editingAnswer) {
      await updateAnswer(editingAnswer.id, data);
    } else {
      await createAnswer(data);
    }
  };

  const handleVerifyAnswer = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await verifyAnswerFreshness(id, 180);
    } catch (err) {
      console.error("Failed to verify answer freshness:", err);
    }
  };

  const handleToggleRetire = async (ans: Answer, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (ans.status === "retired") {
        await restoreAnswer(ans.id);
      } else {
        await retireAnswer(ans.id);
      }
    } catch (err) {
      console.error("Failed to toggle answer retirement:", err);
    }
  };

  const handleEdit = (ans: Answer, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAnswer(ans);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (ans: Answer, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingAnswer(ans);
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      await deleteAnswer(id);
      setDeletingAnswer(null);
    } catch (err) {
      console.error("Failed to delete answer:", err);
      throw err;
    }
  };

  const handleRetireInstead = async (ans: Answer) => {
    try {
      await retireAnswer(ans.id);
      setDeletingAnswer(null);
    } catch (err) {
      console.error("Failed to retire answer:", err);
      throw err;
    }
  };

  const activeCount = answers.filter((a) => a.status !== "retired").length;
  const winningCount = answers.filter(
    (a) => a.status !== "retired" && (a.wonBidCount || 0) > 0,
  ).length;
  const reviewDueCount = answers.filter((a) => {
    if (a.status === "retired") return false;
    const f = calculateFreshness(a.reviewDueDate, a.status);
    return f.isDueSoon || f.isOverdue;
  }).length;
  const retiredCount = answers.filter((a) => a.status === "retired").length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Approved Answer Library
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Verified, procurement-ready narrative responses vetted for tone, compliance, and
            competitive edge.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={() => setIsImportModalOpen(true)}
            className="btn-animate inline-flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300 dark:hover:bg-teal-900/60 shadow-2xs"
          >
            <Sparkles className="h-4 w-4" />
            <span>Import Past Proposal</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setEditingAnswer(null);
              setIsModalOpen(true);
            }}
            className="btn-animate inline-flex items-center gap-2 rounded-xl bg-[#2ba5b5] px-4 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-[#238997]"
          >
            <Plus className="h-4 w-4" />
            <span>New Library Answer</span>
          </button>
        </div>
      </div>

      {/* Filter and Status Controls */}
      <div className="glass flex flex-col gap-4 rounded-xl p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search approved answers by title, narrative, concept, or source..."
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-900 placeholder-slate-400 outline-hidden focus:border-[#2ba5b5] focus:ring-2 focus:ring-[#2ba5b5]/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          {/* Status Tabs */}
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
            <button
              type="button"
              onClick={() => setStatusFilter("all_active")}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                statusFilter === "all_active"
                  ? "bg-white text-slate-900 shadow-2xs dark:bg-slate-700 dark:text-white"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              Active ({activeCount})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("winning")}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                statusFilter === "winning"
                  ? "bg-amber-500 text-white shadow-2xs"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              <Trophy className="h-3 w-3" />
              <span>Winning ({winningCount})</span>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("review_due")}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                statusFilter === "review_due"
                  ? "bg-amber-500 text-white shadow-2xs"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              Review Due ({reviewDueCount})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("retired")}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                statusFilter === "retired"
                  ? "bg-white text-slate-900 shadow-2xs dark:bg-slate-700 dark:text-white"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              Retired ({retiredCount})
            </button>
          </div>
        </div>

        {/* Tag Filters */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-200/60 pt-3 dark:border-slate-800/60">
            <span className="mr-1 text-[11px] font-semibold uppercase text-slate-400">
              Filter By Service:
            </span>
            <button
              type="button"
              onClick={() => setSelectedTag(null)}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-all ${
                selectedTag === null
                  ? "bg-[#2ba5b5] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              All Tags
            </button>
            {allTags.map((tag) => {
              const isSelected = selectedTag === tag;
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setSelectedTag(isSelected ? null : tag)}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-all ${
                    isSelected
                      ? "bg-[#2ba5b5] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Answer Cards List */}
      {loading ? (
        <div className="glass flex items-center justify-center rounded-2xl p-12 text-slate-400">
          <RefreshCw className="h-6 w-6 animate-spin text-[#2ba5b5]" />
          <span className="ml-3 text-sm">Loading approved answer library...</span>
        </div>
      ) : filteredAnswers.length === 0 ? (
        <div className="glass flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 p-12 text-center dark:border-slate-800">
          <div className="rounded-full bg-teal-50 p-3 text-[#2ba5b5] dark:bg-teal-950/60">
            <BookOpen className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-800 dark:text-slate-200">
            {answers.length === 0
              ? "Approved Answer Library is Empty"
              : "No answers match your search filter"}
          </h3>
          <p className="mt-1 max-w-md text-xs text-slate-500 dark:text-slate-400">
            {answers.length === 0
              ? "Seed or create verified narrative answers ready for proposal drafting. Each answer preserves human authorship, provenance citations, and review dates."
              : "Try adjusting your search terms, clearing service tag filters, or selecting a different status tab."}
          </p>
          {answers.length === 0 && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setIsImportModalOpen(true)}
                className="btn-animate inline-flex items-center gap-2 rounded-xl bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238997]"
              >
                <Sparkles className="h-4 w-4" />
                <span>Import Past Proposal</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingAnswer(null);
                  setIsModalOpen(true);
                }}
                className="btn-animate inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 shadow-2xs"
              >
                <Plus className="h-4 w-4" />
                <span>Add First Approved Answer</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAnswers.map((ans) => {
            const freshness = calculateFreshness(ans.reviewDueDate, ans.status);

            return (
              <ExpandableCard
                key={ans.id}
                title={ans.title}
                badge={freshness.badgeLabel}
                badgeColor={freshness.badgeColor}
                summary={
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    {(ans.wonBidCount || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                        <Trophy className="h-3 w-3 text-amber-500" />
                        <span>
                          Won in {ans.wonBidCount}{" "}
                          {ans.wonBidCount === 1 ? "proposal" : "proposals"}
                        </span>
                      </span>
                    )}
                    {ans.harvestedFrom && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-2 py-0.5 text-xs font-medium text-[#2ba5b5] dark:bg-teal-950/40">
                        <Sparkles className="h-3 w-3" />
                        <span>Harvested</span>
                      </span>
                    )}
                    <span className="flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300">
                      <Tag className="h-3 w-3 text-[#2ba5b5]" />
                      {ans.serviceTags.length > 0
                        ? ans.serviceTags.join(", ")
                        : "General / Uncategorized"}
                    </span>
                    <span>•</span>
                    <span>Owner: {ans.owner}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Review Due: {ans.reviewDueDate || "Not set"}
                    </span>
                  </div>
                }
                details={
                  <div className="space-y-4">
                    {/* Full Narrative Text */}
                    <div className="rounded-lg bg-slate-50 p-4 text-sm leading-relaxed text-slate-800 dark:bg-slate-800/60 dark:text-slate-200 font-sans whitespace-pre-wrap">
                      {ans.body}
                    </div>

                    {/* Footer Info & Actions */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        {ans.sourceProposal && (
                          <span>
                            Source:{" "}
                            <span className="font-semibold text-slate-700 dark:text-slate-300">
                              {ans.sourceProposal}
                            </span>
                            {ans.sourceOutcome && (
                              <span className="ml-1 text-[11px] font-bold uppercase text-slate-500">
                                ({ans.sourceOutcome})
                              </span>
                            )}
                          </span>
                        )}
                        {ans.harvestedFrom && (
                          <span>
                            Client:{" "}
                            <span className="font-semibold text-slate-700 dark:text-slate-300">
                              {ans.harvestedFrom.clientName}
                            </span>
                          </span>
                        )}
                        <span>•</span>
                        <span>Verified: {ans.lastVerifiedDate || "Unknown"}</span>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {/* Copy Narrative Button */}
                        <CopyNarrativeButton textToCopy={ans.body} label="Copy Narrative" />

                        {/* Verify Freshness Button */}
                        {ans.status !== "retired" && (
                          <button
                            type="button"
                            onClick={(e) => handleVerifyAnswer(ans.id, e)}
                            title="Confirm this response is accurate and extend review due date by 6 months"
                            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800/60 dark:bg-teal-950/40 dark:text-teal-300 dark:hover:bg-teal-900/60"
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            <span>Verify as Current</span>
                          </button>
                        )}

                        {/* Edit Button */}
                        <button
                          type="button"
                          onClick={(e) => handleEdit(ans, e)}
                          className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        >
                          <Edit2 className="h-3.5 w-3.5 text-slate-400" />
                          <span>Edit</span>
                        </button>

                        {/* Retire / Restore Button */}
                        <button
                          type="button"
                          onClick={(e) => handleToggleRetire(ans, e)}
                          title={
                            ans.status === "retired"
                              ? "Restore to active library"
                              : "Retire from active proposals"
                          }
                          className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                        >
                          {ans.status === "retired" ? (
                            <>
                              <RotateCcw className="h-3.5 w-3.5" />
                              <span>Restore</span>
                            </>
                          ) : (
                            <>
                              <Archive className="h-3.5 w-3.5" />
                              <span>Retire</span>
                            </>
                          )}
                        </button>

                        {/* Delete Button */}
                        <button
                          type="button"
                          onClick={(e) => handleDeleteClick(ans, e)}
                          title="Delete answer permanently"
                          className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-rose-900/60 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                }
              />
            );
          })}
        </div>
      )}

      {/* Add / Edit Answer Modal */}
      <AnswerModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAnswer(null);
        }}
        onSave={handleSaveAnswer}
        onDelete={handleConfirmDelete}
        initialData={editingAnswer}
        staff={staff}
      />

      {/* Delete Confirmation Modal */}
      <DeleteAnswerModal
        isOpen={Boolean(deletingAnswer)}
        onClose={() => setDeletingAnswer(null)}
        answer={deletingAnswer}
        onConfirmDelete={handleConfirmDelete}
        onRetireInstead={handleRetireInstead}
      />

      {/* Import Past Proposal Modal */}
      <ImportProposalModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        user={user || null}
        staff={staff}
      />
    </div>
  );
};
